import { PermissionChartTemplate } from "app/storybookComponents/Charts/types";
import {
  VisibilityDefaultType,
  GetCelebrateAndSupportUsersPayload,
  HrConcernAlert,
} from "./types";

// Display name only. Segment event payloads ("Weekly Check-In Activity Table
// (Admin)" and siblings) stay frozen on the old wording to preserve continuity
// in existing analytics dashboards — never derive an event name from this.
export const WEEKLY_CHECK_IN_FEATURE_NAME = "Manager Check-in";

export const CHECK_IN_TIME_RANGE_OPTIONS: Record<string, string> = {
  "Past 7 days": "pastWeek",
  "Past 30 days": "pastMonth",
  "Past 365 days": "pastYear",
};

export const CHECK_IN_EMAIL_REPORT_TYPES: Record<string, string> = {
  all: "All Reports",
  teamLeader: "Team Leader",
  seniorManager: "Senior Manager",
};

export const HR_CONCERN_SEVERITY_MAP: Record<
  HrConcernAlert["severity"],
  { label: string; className: string; sortValue: number }
> = {
  critical: {
    label: "Critical",
    className: "concern-badge concern-badge--critical",
    sortValue: 4,
  },
  high: {
    label: "High",
    className: "concern-badge concern-badge--high",
    sortValue: 3,
  },
  medium: {
    label: "Medium",
    className: "concern-badge",
    sortValue: 2,
  },
  low: {
    label: "Low",
    className: "concern-badge",
    sortValue: 1,
  },
};

const RESPONSE_SUMMARY_KEY = "Response Summary";
const CHECK_IN_SCORES_KEY =
  "Check-In Number Scores (e.g. Mood, Progress, Readiness)";

export const WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_FOR_PRIVACY_FOCUSED: PermissionChartTemplate =
  {
    [RESPONSE_SUMMARY_KEY]: {
      admin: true,
      usersManager: true,
      teamLeaders: false,
      reportingLineManagers: false,
      everyoneElse: false,
    },
    [CHECK_IN_SCORES_KEY]: {
      admin: true,
      usersManager: true,
      teamLeaders: true,
      reportingLineManagers: true,
      everyoneElse: false,
    },
  };

export const WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_FOR_TEAM_FOCUSED: PermissionChartTemplate =
  {
    [RESPONSE_SUMMARY_KEY]: {
      admin: true,
      usersManager: true,
      teamLeaders: true,
      reportingLineManagers: false,
      everyoneElse: false,
    },
    [CHECK_IN_SCORES_KEY]: {
      admin: true,
      usersManager: true,
      teamLeaders: true,
      reportingLineManagers: true,
      everyoneElse: false,
    },
  };

export const WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_FOR_DEVELOPMENT_FOCUSED: PermissionChartTemplate =
  {
    [RESPONSE_SUMMARY_KEY]: {
      admin: true,
      usersManager: true,
      teamLeaders: true,
      reportingLineManagers: true,
      everyoneElse: false,
    },
    [CHECK_IN_SCORES_KEY]: {
      admin: true,
      usersManager: true,
      teamLeaders: true,
      reportingLineManagers: true,
      everyoneElse: false,
    },
  };

export const WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS: Record<
  VisibilityDefaultType,
  PermissionChartTemplate
> = {
  "development-focused":
    WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_FOR_DEVELOPMENT_FOCUSED,
  "team-focused": WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_FOR_TEAM_FOCUSED,
  "privacy-focused": WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_FOR_PRIVACY_FOCUSED,
};

export const WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS = {
  PRIVACY: "privacy-focused",
  TEAM: "team-focused",
  DEVELOPMENT: "development-focused",
} as const;

export const WEEKLY_CHECK_IN_VISIBILITY_LABELS: Record<
  VisibilityDefaultType,
  string
> = {
  "privacy-focused": "Privacy Focused",
  "team-focused": "Team Focused",
  "development-focused": "Development Focused",
};

interface VisibilityDefaultReportInformation {
  bestForText: string;
  settingDescriptionText: string;
  benefitsList: string[];
  emailReportSection: React.ReactNode;
  warningModalDescription: string;
}

export const WEEKLY_CHECK_IN_VISIBILITY_DEFAULT_REPORT_INFORMATION: Record<
  VisibilityDefaultType,
  VisibilityDefaultReportInformation
> = {
  "development-focused": {
    settingDescriptionText:
      "Maximizes coaching impact through appropriate transparency across leadership levels. Recommended setting for most organizations to realize full value.",
    bestForText:
      "Organizations focused on people development, companies with strong feedback culture, larger organizations needing cross-team coordination.",
    benefitsList: [
      "Comprehensive leadership intelligence at all levels",
      "Senior leaders spot cross-team trends early",
      "Enables proactive interventions before issues escalate",
      "Maximum ROI from check-in system",
    ],
    emailReportSection: (
      <p>
        Generated email reports include a user’s focus, updates, and needs each
        week based on their check-in response. You’ve enabled the{" "}
        <b>Senior Manager, Team Leader, and Executive Check-in Report.</b> The
        people who have access to these email reports are shown below:
      </p>
    ),
    warningModalDescription:
      "A user’s response summary will be visible to their direct manager, team leader(s), reporting line managers, and admins. You can enable Senior Manager and Team Leader Check-in Reports from your organization settings.",
  },
  "team-focused": {
    settingDescriptionText:
      "Extends visibility to team leaders. A stepping stone toward full transparency for organizations building feedback culture.",
    bestForText:
      "Mid-sized organizations with team-based structures, organizations with independent business units.",
    benefitsList: [
      "Team leaders gain visibility for team-wide patterns",
      "Protects individual privacy across departments",
      "Maintains good insight flow for team coordination",
    ],
    emailReportSection: (
      <p>
        Generated email reports include a user’s focus, updates, and needs each
        week based on their check-in response. You’ve enabled the{" "}
        <b>Team Leader Check-in Report and Executive Check-in Report.</b> The
        people who have access to these email reports are shown below:
      </p>
    ),
    warningModalDescription:
      "A user’s response summary will only be visible to their direct manager, team leader(s), and admins. Senior Manager Check-in Reports will be automatically disabled for your organization. You can enable Team Leader Check-in Reports from your organization settings.",
  },
  "privacy-focused": {
    settingDescriptionText:
      "Limits visibility to direct managers only. For organizations with strict regulatory requirements or early in feedback journey.",
    bestForText:
      "Organizations with strict privacy requirements, cultures where direct managers are primary support channel.",
    benefitsList: [
      "Fewest people see employee's direct feedback",
      "Reduced concerns in privacy-focused organizations",
      "Appropriate for highly regulated industries",
    ],
    emailReportSection: (
      <p>
        Generated email reports include a user’s focus, updates, and needs each
        week based on their check-in response. You’ve enabled the{" "}
        <b>Executive Check-in Report.</b> The people who have access to these
        email reports are shown below:
      </p>
    ),
    warningModalDescription:
      "A user’s response summary will only be visible to their direct manager and admins. Senior Manager Check-in and Team Leader Check-in Reports will be automatically disabled for your organization.",
  },
};

export const WEEKLY_CHECK_IN_TRAIT_NAMES = {
  moodScore: "mood",
  readinessScore: "readiness",
  progressScore: "progress",
} as const;

export const GetCelebrateAndSupportUsersPayloadTimeIntervals: {
  [key in GetCelebrateAndSupportUsersPayload["timeFrame"]]: {
    dropdownLabel: string;
    timeUnit: string;
  };
} = {
  "7Days": { dropdownLabel: "Past 7 days", timeUnit: "week" },
  "30Days": { dropdownLabel: "Past 30 days", timeUnit: "month" },
  "365Days": { dropdownLabel: "Past 365 days", timeUnit: "year" },
};

export const WEEKLY_CHECK_IN_PATTERN_CONTEXT = {
  "No Issues":
    "No low scores among the last 4 check-ins or across recent check-in cycles.",
  "Things Improving":
    "Low scores appeared in 2 or more of the last 3 check-in cycles, but none of the last 4 check-ins were low. Worth a brief check-in to confirm things are on the right track.",
  "Recent Challenge":
    "2 or more of the last 4 check-ins showed low mood or overall scores. This may be a temporary rough patch — a genuine conversation can help.",
  "May Need Support":
    "2 or more of the last 4 check-ins showed low scores, and this pattern has persisted across 2 or more of the last 3 check-in cycles. This recurring pattern suggests this person may benefit from extra support or a meaningful 1:1.",
} as const;
